import React, { useCallback, useContext, useState } from 'react';
import { connect } from 'react-redux';

import CheckIcon from 'mdi-react/CheckboxMarkedCircleIcon';
import ErrorIcon from 'mdi-react/CloseCircleIcon';

import { AppContext } from '@prisjakt/ui-app-context';
import { colors } from '@prisjakt/ui-base';
import { priceLabel } from '@prisjakt/ui-price-label/src/types';
import Spinner from '@prisjakt/ui-spinner';
import StoreLogo, { VARIANTS } from '@prisjakt/ui-store-logo';

import { reportWrongPrice as reportWrongPriceAction } from '../../../../../areas/feedback/actions';

import { postFeedback as feedbackRequest } from '../../../../../globals/api/requests';

import Modal from '../../../../modal';
import StaticIntercom from '../../../../static-intercom';

import { Price, StockStatus } from '../../../../../types';
import { Metadata, ReportType } from '../types';

import { SHOP_LOGO_WIDTH } from '../../../../../areas/store/constants';

import * as S from './styles';

type OwnProps = {
  closeModal: () => void;
  isOpen: boolean;
  onClose?: () => void;
  priceId?: string | null;
  priceValue?: number | null;
  priceStore: Price['store'];
  productImage?: string | null;
  productName?: string | null;
  stockStatus?: StockStatus | null;
};

type DispatchProps = {
  reportWrongPriceDispatch: (metadata: Metadata) => {};
};

type Props = OwnProps & DispatchProps;

const ReportModal = ({
  closeModal,
  isOpen,
  onClose,
  priceId,
  priceValue,
  priceStore,
  productImage,
  productName,
  reportWrongPriceDispatch,
  stockStatus,
}: Props) => {
  const { market, t } = useContext(AppContext);

  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [sending, setSending] = useState(false);

  const submitted = success || error || sending;

  const postFeedback = async (data: Metadata) => {
    const res = await feedbackRequest(data);

    if (res.data.feedback.success === true) {
      return res;
    }
    if (res.errors) {
      throw new Error(
        `Error creating report on price: ${JSON.stringify(res.errors)}`
      );
    }

    throw new Error('Error creating report on price.');
  };

  const onReport = useCallback(
    (e: React.MouseEvent, type: ReportType) => {
      if (!IS_BROWSER) return;

      // Stops click out action event
      e.preventDefault();
      e.stopPropagation();

      const message = `Error report on ${window.location.href}`;

      const metadata = {
        message,
        agent_type_key: type,
        uri: window.location.href,
        country_code: market.code,
        store_id: priceStore && priceStore.id,
        price_id: parseInt(priceId as string, 10),
        price_value: String(priceValue),
        stock_status: stockStatus,
      } as Metadata;

      reportWrongPriceDispatch(metadata); // Action event for tracking

      setSending(true);
      postFeedback(metadata)
        .then(() => {
          setSending(false);
          setSuccess(true);
        })
        .catch(() => {
          setSending(false);
          setError(true);
        })
        .then(() => {
          setTimeout(() => {
            setSuccess(false);
            return onClose ? onClose() : closeModal();
          }, 3000);
        });
    },
    [
      market.code,
      onClose,
      priceId,
      priceStore,
      priceValue,
      reportWrongPriceDispatch,
      stockStatus,
    ]
  );

  const reportWrongPrice = useCallback(
    (e: React.MouseEvent) => onReport(e, ReportType.WrongPrice),
    [onReport]
  );
  const reportWrongProduct = useCallback(
    (e: React.MouseEvent) => onReport(e, ReportType.WrongProduct),
    [onReport]
  );
  const reportWrongStockStatus = useCallback(
    (e: React.MouseEvent) => onReport(e, ReportType.WrongStockStatus),
    [onReport]
  );

  const highResolutionLogo = priceStore?.logo
    ? { url: priceStore.logo, width: SHOP_LOGO_WIDTH }
    : null;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      shouldShowCloseIcon={!submitted}
      width={1024}
    >
      <S.Content>
        {submitted ? (
          <S.SubmitMessage>
            {sending && <Spinner isActive position="none" size={30} />}

            {success && (
              <S.Message>
                <CheckIcon color={colors.green1} size="44" />
                {t('feedback', 'dialog.thanks-for-feedback')}
              </S.Message>
            )}
            {error && (
              <S.Message>
                <ErrorIcon color={colors.red1} size="44" />
                {t('feedback', 'dialog.request-error')}
              </S.Message>
            )}
          </S.SubmitMessage>
        ) : (
          <>
            <S.Title>
              {priceStore && priceStore.hasLogo && (
                <StoreLogo
                  name={priceStore.name}
                  storeId={priceStore.id}
                  featured={Boolean(priceStore.featured)}
                  hasImage={priceStore.hasLogo}
                  variant={VARIANTS.PRICE_LIST}
                  logoUrl={highResolutionLogo?.url}
                  width={highResolutionLogo?.width}
                  showHighResolutionLogo={Boolean(highResolutionLogo)}
                />
              )}
            </S.Title>

            <S.Card>
              {productImage && (
                <S.ProductImage src={productImage} name={productName} />
              )}
              <S.ProductName>{productName}</S.ProductName>
              <S.ReportDescription thin>
                {t('feedback', 'report.wrong-product-description')}
              </S.ReportDescription>
              <S.Button color="primary" onClick={reportWrongProduct} truncate>
                {t('feedback', 'report.wrong-product')}
              </S.Button>
            </S.Card>

            <S.Card>
              <S.PriceLabel value={priceValue} styling={priceLabel.PRIMARY} />
              <S.ReportDescription thin>
                {t('feedback', 'report.wrong-price-description')}
              </S.ReportDescription>

              <S.Button color="primary" onClick={reportWrongPrice} truncate>
                {t('feedback', 'report.wrong-price')}
              </S.Button>
            </S.Card>

            <S.Card>
              <S.StockStatus
                status={stockStatus}
                classicIcons
                showText={false}
                width={48}
                height={22}
              />
              <S.ReportDescription thin>
                {t('feedback', 'report.wrong-stock-status-description')}
              </S.ReportDescription>
              <S.Button
                color="primary"
                onClick={reportWrongStockStatus}
                truncate
              >
                {t('feedback', 'report.wrong-stock-status')}
              </S.Button>
            </S.Card>

            <S.Card>
              <S.ReportDescription>
                {t('feedback', 'report.other-feedback-description')}
              </S.ReportDescription>

              <StaticIntercom
                customButton={
                  <S.FeedbackButton
                    title={t('feedback', 'report.other-feedback')}
                    variant="outlined"
                    color="primary"
                  >
                    <S.ButtonText>
                      {t('feedback', 'report.other-feedback')}
                    </S.ButtonText>
                  </S.FeedbackButton>
                }
              />
            </S.Card>
          </>
        )}
      </S.Content>
    </Modal>
  );
};

const dispatchProps = {
  reportWrongPriceDispatch: reportWrongPriceAction,
};

export default connect(null, dispatchProps)(ReportModal);
